import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { unpinAll, unpinMessages } from 'APP/Tasks/messaging/pinUnpinMessage/pinUnpinMessage';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';

interface IAllPinsFooterPresenter {
  groupId: string | null;
  group: Group | null;
  pinnedMessages: ChatMessage[];
  count: number;
  text: string;
  canUnpinMessages: boolean;
  setGroupId(groupId: string): void;
  onBack(): void;
  unpin(): Promise<void>;
}

export const useAllPinsFooterPresenter = (groupId: string): IAllPinsFooterPresenter => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const presenter = useLocalObservable<IAllPinsFooterPresenter>(() => ({
    groupId: null,

    get group(): Group | null {
      if (!presenter.groupId) {
        return null;
      }
      return Entities.GroupsStore.getGroupById(presenter.groupId);
    },

    get pinnedMessages(): ChatMessage[] {
      return presenter.group?.pinnedMessages.messages || [];
    },

    get count(): number {
      if (presenter.group?.messagesStore.multiSelect.hasSelectedMessages) {
        return presenter.group?.messagesStore.multiSelect.selectedMessages.length;
      }

      return presenter.pinnedMessages.length;
    },

    get text(): string {
      if (presenter.count > 1) {
        return t('multiple_pin_all', { 0: presenter.count });
      }

      return t('multiple_pin_single');
    },

    setGroupId(groupId: string): void {
      presenter.groupId = groupId;
    },

    onBack(): void {
      if (!presenter.group) {
        return;
      }
      navigate(presenter.group.routePath);
    },

    get canUnpinMessages(): boolean {
      return !!presenter.group?.withMeInAdmins;
    },

    async unpin(): Promise<void> {
      const confirmResult = await Tasks.app.showConfirm({
        text: t('common_unpin_all_messages_confirmation'),
        primaryText: t('common_yes'),
        secondaryText: t('common_no'),
      });

      if (confirmResult) {
        if (presenter.group?.messagesStore.multiSelect.hasSelectedMessages) {
          const selectedMessages = presenter.group?.messagesStore.multiSelect.selectedMessages;
          const isAllSelected = presenter.pinnedMessages.length === selectedMessages.length;

          unpinMessages(groupId, selectedMessages);

          presenter.group.messagesStore.multiSelect.clearSelectedMessages();
          if (isAllSelected) {
            presenter.onBack();
          }
        } else {
          unpinAll(groupId);
          Entities.toast.show(t('common_unpin_all_messages'));
          presenter.onBack();
        }
      }
    },
  }));

  useEffect(() => {
    presenter.setGroupId(groupId);
  }, [groupId]);

  return presenter;
};
